import React, { useEffect, useState } from "react";
import { Box, Drawer, LinearProgress, Skeleton, Typography } from "@mui/material";
import { InsightsOutlined as AdherenceIcon } from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, SegmentedControl, EmptyState } from "../../../atoms";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import { useMedicationAdherenceMutation } from "../../../../store/medicationPlanApi";
import useTerms from "../../../../hooks/useTerms";

const fmt = (d) =>
  new Date(d).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** Uyum yüzdesinin rengi: ≥80 iyi, ≥50 dikkat, altı kötü. */
export const adherenceTone = (percent) =>
  percent == null
    ? COLORS.slate500
    : percent >= 80
      ? COLORS.successDark
      : percent >= 50
        ? COLORS.warningDark
        : COLORS.errorDark;

const ROW_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 3,
  py: 1.1,
  borderBottom: `1px solid ${COLORS.slate100}`,
};

// Drawer gövdesi px 3 hizasında grup başlığı
function GroupLabel({ children, count, first = false }) {
  return (
    <Typography
      sx={{
        px: 3,
        pt: first ? 1.5 : 2,
        pb: 0.75,
        fontSize: TYPO.caption,
        fontWeight: 600,
        color: COLORS.slate500,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderTop: first ? "none" : `1px solid ${COLORS.slate100}`,
      }}
    >
      {children}
      {count ? (
        <Box component="span" sx={{ color: COLORS.slate400, fontWeight: 500 }}>
          {` · ${count}`}
        </Box>
      ) : null}
    </Typography>
  );
}

function Row({ time, name, meta }) {
  return (
    <Box sx={ROW_SX}>
      <Typography sx={{ width: 112, flexShrink: 0, fontSize: TYPO.label, color: COLORS.slate500, fontVariantNumeric: "tabular-nums" }}>
        {time}
      </Typography>
      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800 }}>
        {name}
      </Typography>
      {meta ? <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>{meta}</Typography> : null}
    </Box>
  );
}

/**
 * Hastanın ilaç uyumu ayrıntısı (sağdan drawer): 7/30 gün yüzdesi, kaçırılan
 * dozlar ve hasta/yakının yaptığı saat değişiklikleri.
 */
export default function AdherenceDrawer({ open, onClose, patientId, token, refreshKey }) {
  const terms = useTerms();
  const [days, setDays] = useState("7");
  const [load, { data, isLoading }] = useMedicationAdherenceMutation();

  useEffect(() => {
    if (open && token && patientId) load({ token, patientId, days: Number(days) });
  }, [open, token, patientId, days, refreshKey, load]);

  const s = data?.data;
  const loading = isLoading || !s;
  const tone = adherenceTone(s?.percent);
  const missed = s?.missedList || [];
  const shifts = s?.shifts || [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<AdherenceIcon />}
        title="İlaç Uyumu"
        subtitle="Vadesi gelen dozların ne kadarı alındı"
        onClose={onClose}
      />

      <Box sx={{ px: 3, pt: 2, pb: 2, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate100}` }}>
        <SegmentedControl
          value={days}
          onChange={setDays}
          ariaLabel="Dönem"
          options={[
            { value: "7", label: "Son 7 gün" },
            { value: "30", label: "Son 30 gün" },
          ]}
        />
        <Box sx={{ mt: 2, display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.1, color: tone, fontVariantNumeric: "tabular-nums" }}>
            {loading ? <Skeleton width={80} /> : s.percent == null ? "—" : `%${s.percent}`}
          </Typography>
          {loading ? (
            <Skeleton width={180} />
          ) : (
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
              {s.taken} alındı · {s.skipped} atlandı · {s.missed} kaçırıldı
            </Typography>
          )}
        </Box>
        {!loading && s.percent != null ? (
          <LinearProgress
            variant="determinate"
            value={s.percent}
            aria-label="Uyum yüzdesi"
            sx={{ mt: 1.25, height: 4, borderRadius: 2, bgcolor: COLORS.slate100, "& .MuiLinearProgress-bar": { bgcolor: tone } }}
          />
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
        {loading ? null : s.expected === 0 && shifts.length === 0 ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pb: 6 }}>
            <EmptyState
              variant="illustrated-sm"
              icon={<AdherenceIcon />}
              title="Bu dönemde vadesi gelen doz yok"
              description="Alarm planındaki dozların saati geldikçe uyum burada hesaplanır."
            />
          </Box>
        ) : (
          <>
            <GroupLabel first count={missed.length}>Kaçırılan dozlar</GroupLabel>
            {missed.length === 0 ? (
              <Typography sx={{ px: 3, py: 1.25, fontSize: TYPO.label, color: COLORS.slate500 }}>
                Bu dönemde kaçırılan doz yok.
              </Typography>
            ) : (
              missed.map((m) => <Row key={`${m.planId}${m.scheduledAt}`} time={fmt(m.scheduledAt)} name={m.name} />)
            )}
            {shifts.length ? (
              <>
                <GroupLabel count={shifts.length}>{terms.patient}/yakın saat değişiklikleri</GroupLabel>
                {shifts.map((h) => (
                  <Row
                    key={`${h.planId}${h.at}`}
                    time={fmt(h.at)}
                    name={h.name}
                    meta={h.byRole === "relative" ? "Yakını" : terms.patient}
                  />
                ))}
              </>
            ) : null}
          </>
        )}
      </Box>

      <DrawerFooter onCancel={onClose} cancelLabel="Kapat" fullWidth />
    </Drawer>
  );
}
